import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

// Project-scoped tabs — require a selected project (disabled until one is
// picked in the header selector). Global tabs need no project.
const PROJECT_ITEMS = [
  { id: 'project-details', label: 'Project Details' },
  { id: 'bill', label: 'Bill of Quantities' },
  { id: 'measurements', label: 'Measurements' },
  { id: 'summary', label: 'Summary' },
  { id: 'versions', label: 'Versions' },
];
const GLOBAL_ITEMS = [
  { id: 'library', label: 'Library' },
];
// The Rates dropdown groups the stage-specific rate corpora behind a single
// "Rates" nav entry (like Settings): SD area rates and the DD/TD rate library.
const RATES_ITEMS = [
  { id: 'sd-rates', label: 'SD Rates' },
  { id: 'rates', label: 'TD Rates' },
];
const SETTINGS_ITEMS = [
  { id: 'cost-index', label: 'Cost Index' },
  { id: 'source-files', label: 'Source Files' },
  { id: 'taxonomies', label: 'Taxonomies' },
  { id: 'review-subtrades', label: 'Template' },
  { id: 'clauses', label: 'Standard Clauses' },
];

const PROJECT_TAB_IDS = PROJECT_ITEMS.map((i) => i.id);
const RATES_TAB_IDS = RATES_ITEMS.map((i) => i.id);
const SETTINGS_TAB_IDS = SETTINGS_ITEMS.map((i) => i.id);

function tabStyle(active, disabled) {
  return {
    padding: '0.6rem 1rem',
    border: 'none',
    background: 'transparent',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'Raleway, sans-serif',
    fontSize: '0.95rem',
    color: disabled
      ? 'var(--brio-text-disabled, #bbb)'
      : active ? 'var(--brio-gold, #9F902A)' : 'var(--brio-text, #333)',
    borderBottom: active ? '2px solid var(--brio-gold, #9F902A)' : '2px solid transparent',
    fontWeight: active ? 600 : 400,
    marginBottom: '-1px',
    opacity: disabled ? 0.6 : 1,
  };
}

// Generic nav dropdown — a single tab-styled button that reveals a menu of
// sub-tabs (used for both "Rates" and "Settings"). The button reads active
// whenever the current tab is one of its items.
function DropdownMenu({ label, items, current, onChange }) {
  const [open, setOpen] = useState(false);
  const active = items.some((i) => i.id === current);
  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{ ...tabStyle(active, false), display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
      >
        {label}
        <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 900 }} />
          <div style={{ position: 'absolute', top: 'calc(100% + 2px)', right: 0, zIndex: 1000, background: '#fff', border: '1px solid var(--brio-border, #ccc)', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', borderRadius: 6, padding: '0.3rem', minWidth: 180, fontFamily: 'Raleway, sans-serif' }}>
            {items.map((item) => {
              const isActive = current === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => { setOpen(false); onChange(item.id); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', padding: '0.45rem 0.6rem',
                    border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.88rem',
                    fontFamily: 'Raleway, sans-serif',
                    background: isActive ? 'var(--brio-bg, #f7f7f5)' : 'transparent',
                    color: isActive ? 'var(--brio-gold, #9F902A)' : 'var(--brio-text, #333)',
                    fontWeight: isActive ? 600 : 400,
                  }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'var(--brio-bg, #f7f7f5)'; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function BoqNav({ current, onChange, projectSelected }) {
  return (
    <nav
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: '0.25rem',
        borderBottom: '1px solid var(--brio-border-subtle, #e5e5e5)',
        marginBottom: '1.5rem',
      }}
    >
      {PROJECT_ITEMS.map((item) => {
        const disabled = !projectSelected;
        const active = current === item.id;
        return (
          <button
            key={item.id}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onChange(item.id)}
            title={disabled ? 'Select a project first' : undefined}
            style={tabStyle(active, disabled)}
          >
            {item.label}
          </button>
        );
      })}

      <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--brio-border-subtle, #e5e5e5)', margin: '0.4rem 0.5rem 0' }} />

      {GLOBAL_ITEMS.map((item) => {
        const active = current === item.id;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onChange(item.id)}
            style={tabStyle(active, false)}
          >
            {item.label}
          </button>
        );
      })}

      <DropdownMenu label="Rates" items={RATES_ITEMS} current={current} onChange={onChange} />

      <DropdownMenu label="Settings" items={SETTINGS_ITEMS} current={current} onChange={onChange} />

      {/* Portal target — the project workspace renders the Stage switcher here
          so it sits at the far right of this nav row. */}
      <span id="boq-stage-slot" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', paddingBottom: '0.3rem' }} />
    </nav>
  );
}

export { PROJECT_ITEMS, GLOBAL_ITEMS, RATES_ITEMS, SETTINGS_ITEMS, PROJECT_TAB_IDS, RATES_TAB_IDS, SETTINGS_TAB_IDS };
export default BoqNav;
